import React, { useState, useEffect, useCallback } from 'react';
import { FriendRequest, UserId } from 'coop-adventure-engine';
import friendsService from '../../services/friendsService';
import { CharacterPortrait } from './CharacterPortrait';
import apiService from '../../services/api';

export interface FriendRequestListProps {
  /**
   * Optional target scene for clickable friend requests
   */
  selectionTarget?: string;

  /**
   * Required handler when selectionTarget provided (similar to onInventoryItemSelect)
   */
  onRequestSelect?: (requestId: string, targetScene: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;
}

/**
 * Component for displaying list of friend requests
 * When selectionTarget is provided, requests are clickable and navigate to target scene
 */
export const FriendRequestList: React.FC<FriendRequestListProps> = ({
  selectionTarget,
  onRequestSelect,
  className = '',
  style
}) => {
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cache for user info (requester name and portrait)
  const [userInfoCache, setUserInfoCache] = useState<Map<UserId, { name: string; portraitId?: string }>>(new Map());

  const loadFriendRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const friendRequests = await friendsService.getFriendRequests();
      setRequests(friendRequests);
      
      // Load user info for all requesters that we don't have cached
      setUserInfoCache((prevCache) => {
        const newCache = new Map(prevCache);
        const requestsToLoad = friendRequests.filter(req => !newCache.has(req.requesterId));
        
        // Load user info in parallel
        Promise.all(
          requestsToLoad.map(async (request) => {
            try {
              const userInfo = await apiService.getUserInfo(request.requesterId);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                const cacheEntry: { name: string; portraitId?: string } = {
                  name: userInfo.playerName || userInfo.username || `User ${request.requesterId.substring(0, 8)}`
                };
                if (userInfo.portraitId) {
                  cacheEntry.portraitId = userInfo.portraitId;
                }
                updatedCache.set(request.requesterId, cacheEntry);
                return updatedCache;
              });
            } catch (err) {
              console.error(`Failed to load user info for ${request.requesterId}:`, err);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                updatedCache.set(request.requesterId, {
                  name: `User ${request.requesterId.substring(0, 8)}`
                });
                return updatedCache;
              });
            }
          })
        );
        
        return newCache;
      });
    } catch (err) {
      console.error('Failed to load friend requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load friend requests');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFriendRequests();

    // Listen for friend request updates
    const handleFriendRequestUpdate = () => {
      loadFriendRequests();
    };

    window.addEventListener('friend_request:update', handleFriendRequestUpdate);
    return () => {
      window.removeEventListener('friend_request:update', handleFriendRequestUpdate);
    };
  }, [loadFriendRequests]);

  const handleRequestClick = useCallback(
    (requestId: string) => {
      if (selectionTarget && onRequestSelect) {
        onRequestSelect(requestId, selectionTarget);
      }
    },
    [selectionTarget, onRequestSelect]
  );

  if (isLoading) {
    return (
      <div
        className={`friend-request-list loading ${className}`}
        style={style}
        data-testid="friend-request-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading friend requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`friend-request-list error ${className}`}
        style={style}
        data-testid="friend-request-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div
        className={`friend-request-list empty ${className}`}
        style={style}
        data-testid="friend-request-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-friend-requests">
          <p className="italic text-center text-gray-400">No friend requests</p>
        </div>
      </div>
    );
  }

  // If selectionTarget is provided, render as clickable inline options (similar to SelectableInventoryList)
  if (selectionTarget) {
    return (
      <div
        className={`friend-request-list selectable ${className}`}
        style={style}
        data-testid="friend-request-list-selectable"
      >
        <div
          className="friend-request-list-items flex flex-wrap justify-center"
          style={{ gap: '0.5rem' }}
          data-testid="friend-request-items-list"
        >
          {requests.map((request) => {
            const userInfo = userInfoCache.get(request.requesterId);
            const displayName = userInfo?.name || `User ${request.requesterId.substring(0, 8)}`;
            const portraitId = userInfo?.portraitId;
            
            return (
              <button
                key={request.id}
                onClick={() => handleRequestClick(request.id)}
                className="friend-request-item-button inventory-item-button"
                data-testid={`friend-request-${request.id}`}
                data-request-id={request.id}
                aria-label={`Select friend request from ${displayName}`}
              >
                <div className="friend-request-item-content flex flex-col items-center" style={{ gap: '1rem' }}>
                  <div className="friend-request-item-portrait">
                    <CharacterPortrait
                      portraitId={portraitId}
                      variant="card"
                      alt={displayName}
                    />
                  </div>
                  <div className="friend-request-item-name text-base font-medium text-center px-2" style={{ maxWidth: '256px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Regular (non-selectable) friend request list - read-only display
  return (
    <div
      className={`friend-request-list ${className}`}
      style={style}
      data-testid="friend-request-list"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {requests.map((request) => {
          const userInfo = userInfoCache.get(request.requesterId);
          const displayName = userInfo?.name || `User ${request.requesterId.substring(0, 8)}`;
          const portraitId = userInfo?.portraitId;
          
          return (
            <div
              key={request.id}
              className="friend-request-card"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '1rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
              }}
              data-testid={`friend-request-card-${request.id}`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="flex-shrink-0">
                  <CharacterPortrait
                    portraitId={portraitId}
                    variant="card"
                    alt={displayName}
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white">
                    {displayName}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">Friend Request</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FriendRequestList;

